import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  CreditCard,
  MessageSquare,
  Radio,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import type { PermissionResource } from '@/lib/permissions';
import { cn } from '@/utils/cn';

interface IntegrationCard {
  id: string;
  to: string;
  titleKey: string;
  descriptionKey: string;
  icon: typeof CreditCard;
  permissionResource?: PermissionResource;
}

const INTEGRATIONS: IntegrationCard[] = [
  {
    id: 'pay',
    to: '/settings/payments',
    titleKey: 'settings.integrations.cards.pay_title',
    descriptionKey: 'settings.integrations.cards.pay_description',
    icon: CreditCard,
    permissionResource: 'payments',
  },
  {
    id: 'forms',
    to: '/settings/questionnaires',
    titleKey: 'settings.integrations.cards.forms_title',
    descriptionKey: 'settings.integrations.cards.forms_description',
    icon: ClipboardList,
    permissionResource: 'questionnaires',
  },
  {
    id: 'communicate',
    to: '/settings/communicate',
    titleKey: 'settings.integrations.cards.communicate_title',
    descriptionKey: 'settings.integrations.cards.communicate_description',
    icon: Radio,
  },
  {
    id: 'sms',
    to: '/settings/sms',
    titleKey: 'settings.integrations.cards.sms_title',
    descriptionKey: 'settings.integrations.cards.sms_description',
    icon: MessageSquare,
    permissionResource: 'sms',
  },
  {
    id: 'sygeforsikring',
    to: '/settings/sygeforsikring',
    titleKey: 'settings.integrations.cards.sygeforsikring_title',
    descriptionKey: 'settings.integrations.cards.sygeforsikring_description',
    icon: ShieldCheck,
    permissionResource: 'sygeforsikring',
  },
];

export function IntegrationsBackLink() {
  const { t } = useTranslation();
  return (
    <Link
      to="/settings/integrations"
      className="mb-4 inline-flex items-center text-sm text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
    >
      <ArrowLeft className="mr-1 h-4 w-4" />
      {t('settings.integrations.back')}
    </Link>
  );
}

export function ClinicIntegrations() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();

  const visible = INTEGRATIONS.filter((item) => {
    if (!item.permissionResource) return true;
    if (!activeClinicId) return false;
    return hasPermission(activeClinicId, item.permissionResource, 'view');
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('settings.integrations.title')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('settings.integrations.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {visible.map(({ id, to, titleKey, descriptionKey, icon: Icon }) => (
          <Link
            key={id}
            to={to}
            className={cn(
              'group flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition',
              'hover:border-indigo-300 hover:shadow-md dark:border-gray-700 dark:bg-card-dark dark:hover:border-indigo-500',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                <Icon className="h-5 w-5" />
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400 transition group-hover:text-indigo-500" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">
              {t(titleKey)}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t(descriptionKey)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
